/**
 * Context source builder utility
 *
 * Builds a list of context sources from intelligence data for transcript metadata.
 * Extracted from the former ContextSources React component to keep the utility
 * accessible without carrying dead UI code.
 */

export interface ContextSource {
  type: 'company' | 'person' | 'location' | 'conversation' | 'file' | 'webcam' | 'screen' | 'web'
  label: string
  value?: string
  url?: string
  confidence?: number
  timestamp?: number
}

export function buildContextSources(context: {
  company?: { name?: string; domain?: string }
  person?: { fullName?: string; role?: string }
  location?: { city?: string; country?: string }
  hasConversation?: boolean
  uploadedFiles?: string[]
  hasWebcam?: boolean
  hasScreen?: boolean
  webSources?: Array<{ title: string; url: string }>
}): ContextSource[] {
  const sources: ContextSource[] = []

  if (context.company?.name) {
    const companySource: ContextSource = {
      type: 'company',
      label: 'Company',
      value: context.company.name,
    }
    if (context.company.domain) {
      companySource.url = `https://${context.company.domain}`
    }
    sources.push(companySource)
  }

  if (context.person?.fullName) {
    sources.push({
      type: 'person',
      label: 'Contact',
      value: `${context.person.fullName}${context.person.role ? ` (${context.person.role})` : ''}`,
    })
  }

  if (context.location?.city) {
    sources.push({
      type: 'location',
      label: 'Location',
      value: `${context.location.city}${context.location.country ? `, ${context.location.country}` : ''}`,
    })
  }

  if (context.hasConversation) {
    sources.push({
      type: 'conversation',
      label: 'Previous Messages',
      value: 'Using conversation history',
    })
  }

  if (context.uploadedFiles?.length) {
    context.uploadedFiles.forEach(file => {
      sources.push({
        type: 'file',
        label: 'File',
        value: file,
      })
    })
  }

  if (context.hasWebcam) {
    sources.push({
      type: 'webcam',
      label: 'Webcam',
      value: 'Analyzing visual input',
    })
  }

  if (context.hasScreen) {
    sources.push({
      type: 'screen',
      label: 'Screen Share',
      value: 'Analyzing screen content',
    })
  }

  if (context.webSources?.length) {
    context.webSources.forEach(source => {
      sources.push({
        type: 'web',
        label: 'Web',
        value: source.title,
        url: source.url,
      })
    })
  }

  return sources
}
